import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Message, MessageListFilter, MessageListResponse } from './interfaces';

@Injectable({ providedIn: 'root' })
export class MessagesService {
  private readonly API_URL: string = environment.API_url;
  private messagesCache = new Map<string, MessageListResponse>();

  constructor(private httpClient: HttpClient) {}

  getMessagesByTenantID({
    tenantId,
    page,
    size,
    viewed = '',
    forceRefresh,
  }: MessageListFilter & {
    forceRefresh?: boolean;
  }): Observable<MessageListResponse> {
    const cacheKey = `${tenantId}-${page}-${size}-${viewed}`;

    if (!forceRefresh && this.messagesCache.has(cacheKey)) {
      const cachedData = this.messagesCache.get(cacheKey)!;
      return of(cachedData);
    }

    let params = new HttpParams({
      fromObject: {
        viewed: viewed,
      },
    });

    const url = `${this.API_URL}secure-api/messages/${tenantId}/${page}/${size}`;

    return this.httpClient.get<MessageListResponse>(url, { params }).pipe(
      tap({
        next: (response) => {
          this.messagesCache.set(cacheKey, response);
        },
      })
    );
  }

  markMessageAsReadByID(messageId: string, tenantId: string): Observable<void> {
    if (!tenantId) {
      return throwError(() => new Error('O tenantId é obrigatório'));
    }

    const hasReadMessage = false;

    if (hasReadMessage) {
      return EMPTY;
    }

    const url = `${this.API_URL}secure-api/messages/viewed/${messageId}`;
    return this.httpClient.put<void>(url, null);
  }

  markAllMessagesAsRead(
    messages: Message[],
    tenantId: string
  ): Observable<void[]> {
    if (!tenantId) {
      return throwError(() => new Error('O tenantId é obrigatório'));
    }

    if (messages.length === 0) {
      return EMPTY;
    }

    const requests = messages.map((message) =>
      this.markMessageAsReadByID(message.id, tenantId)
    );

    return forkJoin(requests);
  }

  clearCache(): void {
    this.messagesCache.clear();
  }

  updateCacheEntry(cacheKey: string, response: MessageListResponse): void {
    this.messagesCache.set(cacheKey, response);
  }
}
